/**
 * The first cosmetics catalog (stage L): 170+ items, most of them parameter variations of a small
 * set of procedural pixel styles. All art and sound is generated by the client from `params`; there
 * are no image or sound files. The admin panel can add / override items at runtime (the server
 * merges its stored items over this default list; see docs/GAME.md#cosmetics).
 */
import { flagPixels, type FlagLayout } from "./flagArt";
import type {
  BorderPattern,
  CapitalStyle,
  CosmeticItem,
  CosmeticSource,
  CosmeticType,
  EmoteGlyph,
  FillTexture,
  Hex,
  LocalText,
  MusicTheme,
  NukeStyle,
  ParticleShape,
  PriceHint,
  Rarity,
  SoundVoice,
  TrailStyle,
  VictoryAnimation,
  VictorySound,
  CardBackground,
  CardFrame,
} from "./schema";

/** Default price hint per rarity (the store's admin config has the final say). */
export const PRICE_BY_RARITY: Record<Rarity, PriceHint> = {
  common: { coins: 400 },
  rare: { coins: 1200 },
  epic: { crystals: 150 },
  legendary: { crystals: 300 },
};

/** Price hints of the paid custom creations (Crystals only). */
export const CUSTOM_PRICES: Record<string, PriceHint> = {
  "custom.card": { crystals: 200 },
  "custom.flag": { crystals: 250 },
  "custom.victory": { crystals: 300 },
  "custom.colors": { crystals: 250 },
};

const t = (en: string, nl: string): LocalText => ({ en, nl });

/** Short description per type (one sentence, shared by every item of the type). */
export const TYPE_DESC: Record<CosmeticType, LocalText> = {
  border: t("Your borders get this pattern, and everyone sees it.", "Je grenzen krijgen dit patroon en iedereen ziet het."),
  fill: t("Your land gets this texture, and everyone sees it.", "Je land krijgt deze textuur en iedereen ziet het."),
  colorSet: t("Your land and borders use these colours.", "Je land en grenzen krijgen deze kleuren."),
  expansion: t("These particles fly up where your land grows.", "Deze deeltjes vliegen op waar je land groeit."),
  conquest: t("These particles burst when you take enemy land.", "Deze deeltjes spatten als je vijandig land inneemt."),
  capital: t("Your capital gets this look.", "Je hoofdstad krijgt dit uiterlijk."),
  nuke: t("Your nukes explode in these colours and shapes.", "Je kernbommen ontploffen in deze kleuren en vormen."),
  trail: t("Coming soon: your boats leave this trail on the water.", "Binnenkort: je boten laten dit spoor achter op het water."),
  emote: t("Coming soon: a pixel emote you can send in a game.", "Binnenkort: een pixel-emote die je in een potje kunt sturen."),
  soundPack: t("Coming soon: your own game sounds get this sound.", "Binnenkort: je eigen geluiden klinken zo."),
  music: t("Coming soon: the music you hear gets this theme.", "Binnenkort: de muziek die je hoort krijgt dit thema."),
  victory: t("Everyone sees this when you win.", "Iedereen ziet dit als jij wint."),
  nameColor: t("Your name on the map gets this colour.", "Je naam op de kaart krijgt deze kleur."),
  title: t("A title next to your name in lobbies.", "Een titel naast je naam in lobby's."),
  card: t("Your calling card in lobbies and when you win.", "Je visitekaartje in lobby's en als je wint."),
  flag: t("A pixel flag next to your name in lobbies.", "Een pixelvlag naast je naam in lobby's."),
};

/**
 * Types that are in the catalog and the loadout but not shown in a game yet (no in-game hook).
 * The collection screen marks them "coming soon"; the store should do the same or hide them.
 */
export const COMING_SOON_TYPES: readonly CosmeticType[] = ["trail", "emote", "soundPack", "music"];
export function isComingSoon(type: CosmeticType): boolean {
  return COMING_SOON_TYPES.includes(type);
}

interface Named {
  key: string;
  name: LocalText;
}

const ACCENTS: (Named & { hex: Hex })[] = [
  { key: "gold", name: t("gold", "goud"), hex: "#f2c14e" },
  { key: "silver", name: t("silver", "zilver"), hex: "#d4dae0" },
  { key: "ice", name: t("ice", "ijs"), hex: "#8fd3ff" },
  { key: "ember", name: t("ember", "gloed"), hex: "#ff7a3d" },
];

/** Source per accent (silver items come from levels, ice from the season pass, the rest from the store). */
const SOURCE_BY_ACCENT: Record<string, CosmeticSource> = { gold: "store", silver: "level", ice: "pass", ember: "store" };

const BORDERS: (Named & { pattern: BorderPattern; rarity: Rarity })[] = [
  { key: "stitch", pattern: "stitch", name: t("Stitches", "Steken"), rarity: "common" },
  { key: "beads", pattern: "beads", name: t("Beads", "Kralen"), rarity: "common" },
  { key: "double", pattern: "double", name: t("Double line", "Dubbele lijn"), rarity: "common" },
  { key: "chain", pattern: "chain", name: t("Chain", "Ketting"), rarity: "rare" },
  { key: "zigzag", pattern: "zigzag", name: t("Zigzag", "Zigzag"), rarity: "rare" },
  { key: "brick", pattern: "brick", name: t("Bricks", "Stenen"), rarity: "rare" },
  { key: "rope", pattern: "rope", name: t("Rope", "Touw"), rarity: "epic" },
  { key: "studs", pattern: "studs", name: t("Studs", "Noppen"), rarity: "legendary" },
];

const FILLS: (Named & { texture: FillTexture; rarity: Rarity })[] = [
  { key: "checker", texture: "checker", name: t("Checkers", "Ruitjes"), rarity: "common" },
  { key: "stripes", texture: "stripes", name: t("Stripes", "Strepen"), rarity: "common" },
  { key: "dots", texture: "dots", name: t("Dots", "Stippen"), rarity: "common" },
  { key: "grain", texture: "grain", name: t("Grain", "Korrel"), rarity: "common" },
  { key: "waves", texture: "waves", name: t("Waves", "Golven"), rarity: "rare" },
  { key: "bricks", texture: "bricks", name: t("Masonry", "Metselwerk"), rarity: "rare" },
  { key: "cross", texture: "cross", name: t("Crosses", "Kruisjes"), rarity: "rare" },
  { key: "scales", texture: "scales", name: t("Scales", "Schubben"), rarity: "epic" },
  { key: "diamonds", texture: "diamonds", name: t("Diamonds", "Ruiten"), rarity: "epic" },
  { key: "stars", texture: "stars", name: t("Starfield", "Sterrenveld"), rarity: "legendary" },
];

const COLOR_SETS: (Named & { territory: Hex; border: Hex; rarity: Rarity })[] = [
  { key: "royal", name: t("Royal", "Koninklijk"), territory: "#3a4fd8", border: "#f2c14e", rarity: "rare" },
  // Stage M (visualaudio): forest and glacier moved so the land reads under them and glacier is no sea.
  { key: "forest", name: t("Forest", "Woud"), territory: "#1f8a64", border: "#d9f2a0", rarity: "rare" },
  { key: "lava", name: t("Lava", "Lava"), territory: "#c4321e", border: "#ffd166", rarity: "epic" },
  { key: "glacier", name: t("Glacier", "Gletsjer"), territory: "#9ab8f5", border: "#0e2a47", rarity: "epic" },
  { key: "night", name: t("Night", "Nacht"), territory: "#3b2a6b", border: "#c7b8ff", rarity: "epic" },
  { key: "sand", name: t("Sandstone", "Zandsteen"), territory: "#d9a55b", border: "#5a3412", rarity: "legendary" },
];

const SHAPES: (Named & { shape: ParticleShape; colors: Hex[]; rarity: Rarity })[] = [
  { key: "spark", shape: "spark", name: t("Sparks", "Vonken"), colors: ["#fff3a8", "#ffd166"], rarity: "common" },
  { key: "leaf", shape: "leaf", name: t("Leaves", "Bladeren"), colors: ["#6fcf5a", "#3b9b3a"], rarity: "common" },
  { key: "snow", shape: "snow", name: t("Snowflakes", "Sneeuwvlokken"), colors: ["#ffffff", "#cfe9ff"], rarity: "common" },
  { key: "bubble", shape: "bubble", name: t("Bubbles", "Bellen"), colors: ["#a8e6ff", "#ffffff"], rarity: "rare" },
  { key: "petal", shape: "petal", name: t("Petals", "Bloemblaadjes"), colors: ["#ff9ec7", "#ffd1e3"], rarity: "rare" },
  { key: "coin", shape: "coin", name: t("Coins", "Muntjes"), colors: ["#f2c14e", "#b8862b"], rarity: "epic" },
  { key: "ember", shape: "ember", name: t("Embers", "Sintels"), colors: ["#ff7a3d", "#ffd166", "#c4321e"], rarity: "epic" },
  { key: "star", shape: "star", name: t("Stars", "Sterren"), colors: ["#ffffff", "#ffe27a", "#9fd8ff"], rarity: "legendary" },
];

const CAPITALS: (Named & { style: CapitalStyle; banner: Hex; roof: Hex; rarity: Rarity })[] = [
  { key: "keep", style: "keep", name: t("Stone keep", "Stenen burcht"), banner: "#c4321e", roof: "#6b6f78", rarity: "common" },
  { key: "tower", style: "tower", name: t("High tower", "Hoge toren"), banner: "#3a4fd8", roof: "#8a3b2e", rarity: "common" },
  { key: "windmill", style: "windmill", name: t("Windmill", "Molen"), banner: "#f2c14e", roof: "#7a4a24", rarity: "rare" },
  { key: "dome", style: "dome", name: t("Golden dome", "Gouden koepel"), banner: "#2f8a3e", roof: "#f2c14e", rarity: "rare" },
  { key: "lighthouse", style: "lighthouse", name: t("Lighthouse", "Vuurtoren"), banner: "#c4321e", roof: "#ffffff", rarity: "rare" },
  { key: "pagoda", style: "pagoda", name: t("Pagoda", "Pagode"), banner: "#c4321e", roof: "#2b5c4a", rarity: "epic" },
  { key: "obelisk", style: "obelisk", name: t("Obelisk", "Obelisk"), banner: "#5fb7e0", roof: "#d9a55b", rarity: "epic" },
  { key: "citadel", style: "citadel", name: t("Citadel", "Citadel"), banner: "#7b3fbf", roof: "#3b3f4a", rarity: "legendary" },
];

const NUKES: (Named & { style: NukeStyle; colors: [Hex, Hex, Hex]; rarity: Rarity })[] = [
  { key: "classic", style: "classic", name: t("Classic blast", "Klassieke knal"), colors: ["#fff3a8", "#ff9a3d", "#8a2b12"], rarity: "rare" },
  { key: "ring", style: "ring", name: t("Shock ring", "Schokring"), colors: ["#ffffff", "#8fd3ff", "#2a5fb8"], rarity: "rare" },
  { key: "flower", style: "flower", name: t("Blossom", "Bloesem"), colors: ["#ffffff", "#ff9ec7", "#b8357a"], rarity: "epic" },
  { key: "cube", style: "cube", name: t("Pixel cube", "Pixelkubus"), colors: ["#d9f2a0", "#6fcf5a", "#1f6b2a"], rarity: "epic" },
  { key: "star", style: "star", name: t("Supernova", "Supernova"), colors: ["#ffffff", "#ffe27a", "#7b3fbf"], rarity: "legendary" },
  { key: "spiral", style: "spiral", name: t("Vortex", "Draaikolk"), colors: ["#c7b8ff", "#7b3fbf", "#2b1650"], rarity: "legendary" },
];

const TRAILS: (Named & { style: TrailStyle; colors: Hex[]; length: number; rarity: Rarity })[] = [
  { key: "foam", style: "foam", name: t("Sea foam", "Schuim"), colors: ["#ffffff", "#d4f1ff"], length: 4, rarity: "common" },
  { key: "bubbles", style: "bubbles", name: t("Bubbles", "Bellen"), colors: ["#a8e6ff", "#ffffff"], length: 5, rarity: "common" },
  { key: "ink", style: "ink", name: t("Ink", "Inkt"), colors: ["#1b1f2b", "#3b3f4a"], length: 5, rarity: "rare" },
  { key: "sparkle", style: "sparkle", name: t("Sparkles", "Glitters"), colors: ["#fff3a8", "#ffffff"], length: 6, rarity: "rare" },
  { key: "petals", style: "petals", name: t("Petals", "Bloemblaadjes"), colors: ["#ff9ec7", "#ffd1e3"], length: 6, rarity: "epic" },
  { key: "ember", style: "ember", name: t("Fire wake", "Vuurspoor"), colors: ["#ff7a3d", "#ffd166"], length: 6, rarity: "epic" },
  { key: "stars", style: "stars", name: t("Star wake", "Sterrenspoor"), colors: ["#ffffff", "#ffe27a"], length: 7, rarity: "legendary" },
  { key: "rainbow", style: "rainbow", name: t("Rainbow", "Regenboog"), colors: ["#ff5a5a", "#ffd166", "#6fcf5a", "#5fb7e0", "#a77bff"], length: 8, rarity: "legendary" },
];

const EMOTES: (Named & { glyph: EmoteGlyph; colors: [Hex, Hex]; rarity: Rarity; source: CosmeticSource })[] = [
  { key: "gg", glyph: "gg", name: t("GG", "GG"), colors: ["#ffffff", "#3a4fd8"], rarity: "common", source: "level" },
  { key: "wave", glyph: "wave", name: t("Wave", "Zwaai"), colors: ["#ffd8a8", "#7a4a24"], rarity: "common", source: "level" },
  { key: "laugh", glyph: "laugh", name: t("Laugh", "Lach"), colors: ["#ffd166", "#5a3412"], rarity: "common", source: "store" },
  { key: "heart", glyph: "heart", name: t("Heart", "Hart"), colors: ["#ff5a7a", "#ffffff"], rarity: "common", source: "store" },
  { key: "sweat", glyph: "sweat", name: t("Phew", "Pfoe"), colors: ["#ffd166", "#5fb7e0"], rarity: "rare", source: "store" },
  { key: "zzz", glyph: "zzz", name: t("Sleepy", "Slaperig"), colors: ["#c7b8ff", "#3b2a6b"], rarity: "rare", source: "pass" },
  { key: "fist", glyph: "fist", name: t("Strong", "Sterk"), colors: ["#ffd8a8", "#7a4a24"], rarity: "rare", source: "store" },
  { key: "flag", glyph: "flag", name: t("White flag", "Witte vlag"), colors: ["#ffffff", "#6b6f78"], rarity: "rare", source: "achievement" },
  { key: "shield", glyph: "shield", name: t("Shield up", "Schild op"), colors: ["#5fb7e0", "#f2c14e"], rarity: "epic", source: "pass" },
  { key: "star", glyph: "star", name: t("Star", "Ster"), colors: ["#ffe27a", "#b8862b"], rarity: "epic", source: "store" },
  { key: "skull", glyph: "skull", name: t("Skull", "Schedel"), colors: ["#f0f0f0", "#1b1f2b"], rarity: "epic", source: "achievement" },
  { key: "crown", glyph: "crown", name: t("Crown", "Kroon"), colors: ["#f2c14e", "#c4321e"], rarity: "legendary", source: "store" },
];

const VOICES: (Named & { voice: SoundVoice; pitch: number; rarity: Rarity })[] = [
  { key: "classic", voice: "classic", pitch: 0, name: t("Classic sounds", "Klassieke geluiden"), rarity: "common" },
  { key: "chip", voice: "chip", pitch: 2, name: t("Chiptune", "Chiptune"), rarity: "rare" },
  { key: "wood", voice: "wood", pitch: -2, name: t("Woodblocks", "Houtblokjes"), rarity: "rare" },
  { key: "brass", voice: "brass", pitch: -3, name: t("Brass", "Koper"), rarity: "epic" },
  { key: "glass", voice: "glass", pitch: 5, name: t("Glass", "Glas"), rarity: "epic" },
  { key: "drum", voice: "drum", pitch: -5, name: t("War drums", "Oorlogstrommels"), rarity: "legendary" },
];

const THEMES: (Named & { theme: MusicTheme; rarity: Rarity })[] = [
  { key: "classic", theme: "classic", name: t("Classic score", "Klassieke muziek"), rarity: "common" },
  { key: "heroic", theme: "heroic", name: t("Heroic", "Heldhaftig"), rarity: "rare" },
  { key: "sea", theme: "sea", name: t("Open sea", "Open zee"), rarity: "rare" },
  { key: "desert", theme: "desert", name: t("Desert", "Woestijn"), rarity: "epic" },
  { key: "frost", theme: "frost", name: t("Frost", "Vorst"), rarity: "epic" },
  { key: "mystic", theme: "mystic", name: t("Mystic", "Mystiek"), rarity: "legendary" },
];

const VICTORIES: (Named & { animation: VictoryAnimation; sound: VictorySound; colors: Hex[]; rarity: Rarity })[] = [
  { key: "confetti", animation: "confetti", sound: "chime", name: t("Confetti", "Confetti"), colors: ["#ff5a5a", "#ffd166", "#5fb7e0", "#6fcf5a"], rarity: "rare" },
  { key: "banner", animation: "banner", sound: "drums", name: t("Banner drop", "Vaandel"), colors: ["#c4321e", "#f2c14e"], rarity: "rare" },
  { key: "fireworks", animation: "fireworks", sound: "fanfare", name: t("Fireworks", "Vuurwerk"), colors: ["#ffd166", "#ff5a7a", "#8fd3ff"], rarity: "epic" },
  { key: "stars", animation: "stars", sound: "bells", name: t("Shooting stars", "Vallende sterren"), colors: ["#ffffff", "#ffe27a"], rarity: "epic" },
  { key: "rays", animation: "rays", sound: "fanfare", name: t("Sun rays", "Zonnestralen"), colors: ["#fff3a8", "#ffb84d"], rarity: "legendary" },
  { key: "crown", animation: "crown", sound: "bells", name: t("Crowned", "Gekroond"), colors: ["#f2c14e", "#c4321e"], rarity: "legendary" },
];

const NAME_COLORS: (Named & { color: Hex; outline: Hex; rarity: Rarity })[] = [
  { key: "snow", name: t("Snow white", "Sneeuwwit"), color: "#ffffff", outline: "#1b1f2b", rarity: "common" },
  { key: "sun", name: t("Sunny", "Zonnig"), color: "#ffe27a", outline: "#3b2a12", rarity: "common" },
  { key: "mint", name: t("Mint", "Mint"), color: "#a8f0c6", outline: "#123b24", rarity: "common" },
  { key: "sky", name: t("Sky", "Hemel"), color: "#9fd8ff", outline: "#0e2a47", rarity: "common" },
  { key: "rose", name: t("Rose", "Roos"), color: "#ffb3cf", outline: "#4a1430", rarity: "rare" },
  { key: "lilac", name: t("Lilac", "Lila"), color: "#d2c2ff", outline: "#2b1650", rarity: "rare" },
  { key: "flame", name: t("Flame", "Vlam"), color: "#ffa25a", outline: "#3b1408", rarity: "rare" },
  { key: "gold", name: t("Gold", "Goud"), color: "#f2c14e", outline: "#2b1d05", rarity: "epic" },
  { key: "jade", name: t("Jade", "Jade"), color: "#4fd18b", outline: "#0b2a18", rarity: "epic" },
  { key: "ruby", name: t("Ruby", "Robijn"), color: "#ff6b6b", outline: "#2b0808", rarity: "legendary" },
];

const TITLES: (Named & { rarity: Rarity; source: CosmeticSource })[] = [
  { key: "recruit", name: t("Recruit", "Rekruut"), rarity: "common", source: "default" },
  { key: "settler", name: t("Settler", "Kolonist"), rarity: "common", source: "level" },
  { key: "explorer", name: t("Explorer", "Ontdekker"), rarity: "common", source: "level" },
  { key: "sailor", name: t("Sailor", "Zeevaarder"), rarity: "common", source: "achievement" },
  { key: "strategist", name: t("Strategist", "Strateeg"), rarity: "rare", source: "level" },
  { key: "diplomat", name: t("Diplomat", "Diplomaat"), rarity: "rare", source: "achievement" },
  { key: "merchant", name: t("Merchant", "Koopman"), rarity: "rare", source: "store" },
  { key: "admiral", name: t("Admiral", "Admiraal"), rarity: "epic", source: "achievement" },
  { key: "warlord", name: t("Warlord", "Krijgsheer"), rarity: "epic", source: "pass" },
  { key: "architect", name: t("Architect", "Bouwmeester"), rarity: "epic", source: "store" },
  { key: "emperor", name: t("Emperor", "Keizer"), rarity: "legendary", source: "level" },
  { key: "supporter", name: t("Supporter", "Supporter"), rarity: "legendary", source: "supporter" },
];

const CARDS: (Named & { background: CardBackground; colors: [Hex, Hex]; frame: CardFrame; badges: EmoteGlyph[]; textColor: Hex; rarity: Rarity })[] = [
  { key: "basic", name: t("Basic card", "Basiskaart"), background: "plain", colors: ["#2a3142", "#3b4458"], frame: "thin", badges: [], textColor: "#ffffff", rarity: "common" },
  { key: "dawn", name: t("Dawn", "Dageraad"), background: "sunburst", colors: ["#ffb84d", "#ff7a3d"], frame: "thin", badges: ["star"], textColor: "#2b1408", rarity: "common" },
  { key: "tide", name: t("Tide", "Getij"), background: "waves", colors: ["#2a5fb8", "#5fb7e0"], frame: "thin", badges: ["wave"], textColor: "#ffffff", rarity: "common" },
  { key: "peaks", name: t("Peaks", "Toppen"), background: "mountains", colors: ["#9fd8ff", "#4a5a70"], frame: "pixel", badges: ["flag"], textColor: "#0e2a47", rarity: "rare" },
  { key: "grid", name: t("Blueprint", "Bouwtekening"), background: "grid", colors: ["#1e3a6b", "#5fb7e0"], frame: "pixel", badges: ["shield"], textColor: "#ffffff", rarity: "rare" },
  { key: "banner", name: t("War banner", "Oorlogsvaandel"), background: "stripes", colors: ["#8a1c1c", "#c4321e"], frame: "thick", badges: ["fist", "skull"], textColor: "#ffe27a", rarity: "rare" },
  { key: "board", name: t("Chessboard", "Schaakbord"), background: "checker", colors: ["#1b1f2b", "#e8e8e8"], frame: "thick", badges: ["crown"], textColor: "#f2c14e", rarity: "epic" },
  { key: "cosmos", name: t("Cosmos", "Kosmos"), background: "stars", colors: ["#120c2b", "#3b2a6b"], frame: "ornate", badges: ["star", "zzz"], textColor: "#c7b8ff", rarity: "epic" },
  { key: "royal", name: t("Royal court", "Hofhouding"), background: "sunburst", colors: ["#3b2a6b", "#7b3fbf"], frame: "ornate", badges: ["crown", "heart", "star"], textColor: "#f2c14e", rarity: "legendary" },
  { key: "supporter", name: t("Supporter card", "Supporterkaart"), background: "stripes", colors: ["#b8862b", "#f2c14e"], frame: "ornate", badges: ["heart", "crown"], textColor: "#2b1d05", rarity: "legendary" },
];

const FLAGS: (Named & { layout: FlagLayout; palette: Hex[]; rarity: Rarity })[] = [
  { key: "meadow", layout: "tricolorV", name: t("Meadow", "Weide"), palette: ["#2f8a3e", "#ffd166", "#5fb7e0"], rarity: "common" },
  { key: "coast", layout: "tricolorH", name: t("Coast", "Kust"), palette: ["#3b2a6b", "#d9a55b", "#1f7a5c"], rarity: "common" },
  { key: "north", layout: "cross", name: t("North cross", "Noorderkruis"), palette: ["#1f7a5c", "#ffe27a"], rarity: "common" },
  { key: "saltire", layout: "saltire", name: t("Saltire", "Schuinkruis"), palette: ["#ffd166", "#7b3fbf"], rarity: "common" },
  { key: "canton", layout: "canton", name: t("Canton", "Kanton"), palette: ["#ffffff", "#ffd166", "#1f7a5c"], rarity: "rare" },
  { key: "split", layout: "diagonal", name: t("Split", "Gedeeld"), palette: ["#1b1f2b", "#f2c14e"], rarity: "rare" },
  { key: "sun", layout: "sun", name: t("Rising sun", "Rijzende zon"), palette: ["#5fb7e0", "#ffe27a", "#ff9a3d"], rarity: "rare" },
  { key: "chevron", layout: "chevron", name: t("Chevron", "Keper"), palette: ["#ffffff", "#2f8a3e", "#c4321e"], rarity: "epic" },
  { key: "quarters", layout: "quarters", name: t("Quarters", "Kwartieren"), palette: ["#7b3fbf", "#f2c14e"], rarity: "epic" },
  { key: "stripes", layout: "stripes", name: t("Stripes", "Banen"), palette: ["#1f7a5c", "#ffe27a"], rarity: "legendary" },
];

function priced(rarity: Rarity, source: CosmeticSource): PriceHint | null {
  return source === "store" ? PRICE_BY_RARITY[rarity] : null;
}

function rarityOrder(r: Rarity): number {
  return r === "common" ? 0 : r === "rare" ? 1 : r === "epic" ? 2 : 3;
}

/** Store items for rarer tiers, earned items for common ones (variety for progression). */
function sourceByRarity(r: Rarity, i: number): CosmeticSource {
  if (r === "common") return i % 2 === 0 ? "level" : "store";
  if (r === "rare") return i % 3 === 0 ? "pass" : "store";
  return "store";
}

function buildCatalog(): CosmeticItem[] {
  const items: CosmeticItem[] = [];
  // Border patterns: 8 patterns x 4 accents.
  for (const b of BORDERS) {
    for (const a of ACCENTS) {
      const source = SOURCE_BY_ACCENT[a.key];
      const rarity: Rarity = a.key === "gold" && rarityOrder(b.rarity) < 3 ? (["rare", "epic", "legendary"] as const)[rarityOrder(b.rarity)] : b.rarity;
      items.push({
        id: `border.${b.key}.${a.key}`,
        type: "border",
        rarity,
        name: t(`${b.name.en} · ${a.name.en}`, `${b.name.nl} · ${a.name.nl}`),
        desc: TYPE_DESC.border,
        price: priced(rarity, source),
        source,
        params: { pattern: b.pattern, accent: a.hex },
        set: a.key === "gold" ? "set.gold" : undefined,
      });
    }
  }
  // Fill textures: 10 textures x 2 strengths.
  for (const f of FILLS) {
    for (const strength of [1, 2] as const) {
      const rarity = strength === 2 && f.rarity === "common" ? "rare" : f.rarity;
      const source = sourceByRarity(rarity, items.length);
      items.push({
        id: `fill.${f.key}.${strength}`,
        type: "fill",
        rarity,
        name: t(strength === 1 ? `${f.name.en} · faint` : `${f.name.en} · clear`, strength === 1 ? `${f.name.nl} · zacht` : `${f.name.nl} · duidelijk`),
        desc: TYPE_DESC.fill,
        price: priced(rarity, source),
        source,
        params: { texture: f.texture, strength },
      });
    }
  }
  for (const c of COLOR_SETS) {
    items.push({
      id: `colors.${c.key}`,
      type: "colorSet",
      rarity: c.rarity,
      name: c.name,
      desc: TYPE_DESC.colorSet,
      price: priced(c.rarity, "store"),
      source: "store",
      params: { territory: c.territory, border: c.border },
    });
  }
  for (const kind of ["expansion", "conquest"] as const) {
    SHAPES.forEach((s, i) => {
      const source = sourceByRarity(s.rarity, i + (kind === "conquest" ? 1 : 0));
      items.push({
        id: `${kind}.${s.key}`,
        type: kind,
        rarity: s.rarity,
        name: s.name,
        desc: TYPE_DESC[kind],
        price: priced(s.rarity, source),
        source,
        params: { shape: s.shape, colors: s.colors },
      });
    });
  }
  CAPITALS.forEach((c, i) => {
    const source = sourceByRarity(c.rarity, i);
    items.push({ id: `capital.${c.key}`, type: "capital", rarity: c.rarity, name: c.name, desc: TYPE_DESC.capital, price: priced(c.rarity, source), source, params: { style: c.style, banner: c.banner, roof: c.roof } });
  });
  NUKES.forEach((n, i) => {
    const source = sourceByRarity(n.rarity, i);
    items.push({ id: `nuke.${n.key}`, type: "nuke", rarity: n.rarity, name: n.name, desc: TYPE_DESC.nuke, price: priced(n.rarity, source), source, params: { style: n.style, colors: n.colors } });
  });
  TRAILS.forEach((tr, i) => {
    const source = sourceByRarity(tr.rarity, i);
    items.push({ id: `trail.${tr.key}`, type: "trail", rarity: tr.rarity, name: tr.name, desc: TYPE_DESC.trail, price: priced(tr.rarity, source), source, params: { style: tr.style, colors: tr.colors, length: tr.length } });
  });
  EMOTES.forEach((e, code) => {
    items.push({ id: `emote.${e.key}`, type: "emote", rarity: e.rarity, name: e.name, desc: TYPE_DESC.emote, price: priced(e.rarity, e.source), source: e.source, params: { glyph: e.glyph, colors: e.colors, code } });
  });
  for (const v of VOICES) {
    const source: CosmeticSource = v.key === "classic" ? "default" : "store";
    items.push({ id: `sound.${v.key}`, type: "soundPack", rarity: v.rarity, name: v.name, desc: TYPE_DESC.soundPack, price: priced(v.rarity, source), source, params: { voice: v.voice, pitch: v.pitch } });
  }
  for (const m of THEMES) {
    const source: CosmeticSource = m.key === "classic" ? "default" : m.key === "sea" ? "pass" : "store";
    items.push({ id: `music.${m.key}`, type: "music", rarity: m.rarity, name: m.name, desc: TYPE_DESC.music, price: priced(m.rarity, source), source, params: { theme: m.theme } });
  }
  VICTORIES.forEach((v, i) => {
    const source = sourceByRarity(v.rarity, i);
    items.push({ id: `victory.${v.key}`, type: "victory", rarity: v.rarity, name: v.name, desc: TYPE_DESC.victory, price: priced(v.rarity, source), source, params: { animation: v.animation, sound: v.sound, colors: v.colors } });
  });
  NAME_COLORS.forEach((n, i) => {
    const source = sourceByRarity(n.rarity, i);
    items.push({ id: `name.${n.key}`, type: "nameColor", rarity: n.rarity, name: n.name, desc: TYPE_DESC.nameColor, price: priced(n.rarity, source), source, params: { color: n.color, outline: n.outline } });
  });
  for (const ti of TITLES) {
    items.push({ id: `title.${ti.key}`, type: "title", rarity: ti.rarity, name: ti.name, desc: TYPE_DESC.title, price: priced(ti.rarity, ti.source), source: ti.source, params: { text: ti.name } });
  }
  CARDS.forEach((c, i) => {
    const source: CosmeticSource = c.key === "basic" ? "default" : c.key === "supporter" ? "supporter" : sourceByRarity(c.rarity, i);
    items.push({
      id: `card.${c.key}`,
      type: "card",
      rarity: c.rarity,
      name: c.name,
      desc: TYPE_DESC.card,
      price: priced(c.rarity, source),
      source,
      params: { background: c.background, colors: c.colors, frame: c.frame, badges: c.badges, textColor: c.textColor },
    });
  });
  FLAGS.forEach((f, i) => {
    const source = sourceByRarity(f.rarity, i);
    items.push({ id: `flag.${f.key}`, type: "flag", rarity: f.rarity, name: f.name, desc: TYPE_DESC.flag, price: priced(f.rarity, source), source, params: { pixels: flagPixels(f.layout), palette: f.palette } });
  });
  // The four paid custom creations (editable; the params are the editor's starting point).
  items.push({
    id: "custom.card",
    type: "card",
    rarity: "epic",
    name: t("Your own card", "Eigen kaart"),
    desc: t("Design your own calling card.", "Ontwerp je eigen visitekaartje."),
    price: CUSTOM_PRICES["custom.card"],
    source: "custom",
    editable: true,
    params: { background: "plain", colors: ["#2a3142", "#5fb7e0"], frame: "pixel", badges: [], textColor: "#ffffff", text: "" },
  });
  items.push({
    id: "custom.flag",
    type: "flag",
    rarity: "epic",
    name: t("Your own flag", "Eigen vlag"),
    desc: t("Draw your own 16 by 11 pixel flag.", "Teken je eigen pixelvlag van 16 bij 11."),
    price: CUSTOM_PRICES["custom.flag"],
    source: "custom",
    editable: true,
    params: { pixels: flagPixels("sun"), palette: ["#2a5fb8", "#ffe27a", "#ff9a3d", "#1b1f2b"] },
  });
  items.push({
    id: "custom.victory",
    type: "victory",
    rarity: "legendary",
    name: t("Your own victory", "Eigen overwinning"),
    desc: t("Pick the animation, sound and text of your win.", "Kies de animatie, het geluid en de tekst van je winst."),
    price: CUSTOM_PRICES["custom.victory"],
    source: "custom",
    editable: true,
    params: { animation: "fireworks", sound: "fanfare", colors: ["#ffd166", "#ff5a7a", "#8fd3ff"], text: "" },
  });
  items.push({
    id: "custom.colors",
    type: "colorSet",
    rarity: "epic",
    name: t("Your own colours", "Eigen kleuren"),
    desc: t("Choose your land and border colours.", "Kies de kleuren van je land en grenzen."),
    price: CUSTOM_PRICES["custom.colors"],
    source: "custom",
    editable: true,
    params: { territory: "#3a7bd5", border: "#ffe27a" },
  });
  return items;
}

/** The built-in catalog (frozen). Use `catalogIndex` for lookups. */
export const DEFAULT_CATALOG: readonly CosmeticItem[] = Object.freeze(buildCatalog());

export type CatalogIndex = ReadonlyMap<string, CosmeticItem>;

let defaultIndex: Map<string, CosmeticItem> | null = null;

/** id → item for a catalog (the default one when omitted). */
export function catalogIndex(items: readonly CosmeticItem[] = DEFAULT_CATALOG): CatalogIndex {
  if (items === DEFAULT_CATALOG) {
    if (!defaultIndex) defaultIndex = new Map(DEFAULT_CATALOG.map((i) => [i.id, i]));
    return defaultIndex;
  }
  return new Map(items.map((i) => [i.id, i]));
}

/** Emote item by its wire code (default catalog), or undefined. */
export function emoteByCode(code: number, index: CatalogIndex = catalogIndex()): CosmeticItem | undefined {
  for (const item of index.values()) if (item.type === "emote" && item.params.code === code) return item;
  return undefined;
}
